import React, { useState, useEffect, useRef } from 'react';
import { Settings, Building2, GraduationCap, Award, Save, Camera, Globe, Phone, Mail, MapPin, Calendar, Plus, Edit2, Trash2, Check, CreditCard, Eye, EyeOff, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import { PushNotificationsPanel } from '../../components/PushNotifications';

const DEFAULT_GRADES = [
  { min: 75, max: 100, grade: 'A1', remark: 'Excellent' },
  { min: 70, max: 74, grade: 'B2', remark: 'Very Good' },
  { min: 65, max: 69, grade: 'B3', remark: 'Good' },
  { min: 60, max: 64, grade: 'C4', remark: 'Credit' },
  { min: 55, max: 59, grade: 'C5', remark: 'Credit' },
  { min: 50, max: 54, grade: 'C6', remark: 'Credit' },
  { min: 45, max: 49, grade: 'D7', remark: 'Pass' },
  { min: 40, max: 44, grade: 'E8', remark: 'Pass' },
  { min: 0, max: 39, grade: 'F9', remark: 'Fail' },
];

export default function SettingsPage() {
  const { school, updateSchool } = useAuth();
  const [profile, setProfile] = useState({ name: '', motto: '', address: '', city: '', state: '', country: 'Nigeria', phone: '', email: '', website: '', logo: null });
  const [grades, setGrades] = useState(DEFAULT_GRADES);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingGrades, setSavingGrades] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);

  // Payment settings state
  const [paySettings, setPaySettings] = useState({ gateway: 'paystack', paystack_public_key: '', paystack_secret_key: '', flutterwave_public_key: '', flutterwave_secret_key: '', has_paystack: false, has_flutterwave: false });
  const [savingPay, setSavingPay] = useState(false);
  const [showPS, setShowPS] = useState(false);
  const [showFWS, setShowFWS] = useState(false);

  useEffect(() => {
    if (school) {
      setProfile({ name: school.name || '', motto: school.motto || '', address: school.address || '', city: school.city || '', state: school.state || '', country: school.country || 'Nigeria', phone: school.phone || '', email: school.email || '', website: school.website || '', logo: school.logo || null });
      if (school.logo) setLogoPreview(school.logo);
    }
    loadAcademic();
    loadGradingScale();
    loadPaySettings();
    loadEmailSettings();
  }, [school?.id]);

  const loadEmailSettings = async () => {
    try {
      const res = await api.get('/school/email-settings');
      if (res.success) setEmailSettings(p => ({ ...p, ...res.data }));
    } catch { /* silent */ }
  };

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await api.put('/school/email-settings', {
        provider: emailSettings.provider,
        email_from: emailSettings.email_from,
        resend_api_key: emailSettings.resend_api_key,
        smtp_host: emailSettings.smtp_host,
        smtp_port: emailSettings.smtp_port,
        smtp_user: emailSettings.smtp_user,
        smtp_pass: emailSettings.smtp_pass,
      });
      toast.success('Email settings saved');
      loadEmailSettings();
      setEmailSettings(p => ({ ...p, resend_api_key: '', smtp_pass: '' }));
    } catch { toast.error('Failed to save email settings'); }
    finally { setSavingEmail(false); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      await api.post('/school/email-settings/test', { to: testEmailTo || undefined });
      toast.success('Test email sent! Check your inbox.');
    } catch (err) { toast.error(err?.detail || 'Test email failed — check your settings'); }
    finally { setTestingEmail(false); }
  };

  const loadPaySettings = async () => {
    try {
      const res = await api.get('/fees/payment-settings');
      if (res.success) setPaySettings(p => ({ ...p, ...res.data }));
    } catch { /* silent */ }
  };

  const savePaySettings = async () => {
    setSavingPay(true);
    try {
      await api.put('/fees/payment-settings', {
        gateway: paySettings.gateway,
        paystack_public_key: paySettings.paystack_public_key,
        paystack_secret_key: paySettings.paystack_secret_key,
        flutterwave_public_key: paySettings.flutterwave_public_key,
        flutterwave_secret_key: paySettings.flutterwave_secret_key,
      });
      toast.success('Payment settings saved');
      loadPaySettings();
    } catch { toast.error('Failed to save payment settings'); }
    finally { setSavingPay(false); }
  };

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({ provider: 'none', has_resend: false, has_smtp: false, email_from: '', resend_api_key: '', smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');

  const loadAcademic = async () => {
    try {
      const [yr, tr] = await Promise.all([api.get('/academics/years'), api.get('/academics/terms')]);
      setYears(yr.data || []);
      setTerms(tr.data || []);
    } catch { /* silent */ }
  };

  const loadGradingScale = async () => {
    try {
      const res = await api.get('/school/grading-scale');
      if (res.data?.grades) setGrades(res.data.grades);
    } catch { /* silent */ }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB'); return; }
    setUploadingLogo(true);
    try {
      const url = await uploadToCloudinary(file, 'eduva/schools');
      setLogoPreview(url);
      setProfile(p => ({ ...p, logo: url }));
      toast.success('Logo uploaded');
    } catch {
      toast.error('Logo upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveProfile = async () => {
    if (!profile.name) { toast.error('School name is required'); return; }
    setSavingProfile(true);
    try {
      const res = await api.put('/school/profile', profile);
      updateSchool(res.data);
      toast.success('School profile saved');
    } catch { toast.error('Failed to save profile'); }
    finally { setSavingProfile(false); }
  };

  const saveGrades = async () => {
    setSavingGrades(true);
    try {
      await api.put('/school/grading-scale', { grades });
      toast.success('Grading scale saved');
    } catch { toast.error('Failed to save grading scale'); }
    finally { setSavingGrades(false); }
  };

  const setCurrentYear = async (yearId) => {
    try {
      await api.put(`/academics/years/${yearId}/set-current`, {});
      loadAcademic();
      toast.success('Academic year updated');
    } catch { toast.error('Failed to update'); }
  };

  const setCurrentTerm = async (termId) => {
    try {
      await api.put(`/academics/terms/${termId}/set-current`, {});
      loadAcademic();
      toast.success('Current term updated');
    } catch { toast.error('Failed to update'); }
  };

  const updateGrade = (idx, key, val) => {
    setGrades(prev => prev.map((g, i) => i === idx ? { ...g, [key]: key === 'grade' || key === 'remark' ? val : Number(val) } : g));
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">School Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your school's profile, academic calendar, and grading configuration</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="profile" className="rounded-lg text-sm"><Building2 className="w-3.5 h-3.5 mr-1.5" />Profile</TabsTrigger>
          <TabsTrigger value="academic" className="rounded-lg text-sm"><Calendar className="w-3.5 h-3.5 mr-1.5" />Academic</TabsTrigger>
          <TabsTrigger value="grading" className="rounded-lg text-sm"><Award className="w-3.5 h-3.5 mr-1.5" />Grading Scale</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-lg text-sm" data-testid="tab-payments"><CreditCard className="w-3.5 h-3.5 mr-1.5" />Payments</TabsTrigger>
          <TabsTrigger value="email" className="rounded-lg text-sm" data-testid="tab-email"><Mail className="w-3.5 h-3.5 mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="modules" className="rounded-lg text-sm" data-testid="tab-modules"><Award className="w-3.5 h-3.5 mr-1.5" />Modules</TabsTrigger>
        </TabsList>

        {/* ── Profile Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="profile" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            {/* Logo Upload */}
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-[#eef1ff] border-2 border-[#516EFA]/20 flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="School logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-8 h-8 text-[#516EFA]" />
                  )}
                </div>
                <button
                  onClick={() => !uploadingLogo && logoRef.current?.click()}
                  disabled={uploadingLogo}
                  className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-[#516EFA] text-white rounded-full flex items-center justify-center shadow-sm hover:bg-[#4058e8] transition-colors disabled:opacity-60"
                >
                  {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                </button>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">{profile.name || 'Your School'}</p>
                <p className="text-sm text-slate-500 mt-0.5">{uploadingLogo ? 'Uploading...' : 'Click camera icon to upload (max 5MB)'}</p>
                <p className="text-xs text-indigo-500 font-medium mt-0.5">Appears on your public admission portal</p>
                {logoPreview && !uploadingLogo && (
                  <button onClick={() => { setLogoPreview(null); setProfile(p => ({ ...p, logo: null })); }} className="text-xs text-red-500 hover:underline mt-1">
                    Remove logo
                  </button>
                )}
              </div>
            </div>

            <div className="h-px bg-slate-100" />

            {/* Profile Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">School Name *</Label>
                <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Greenfield Academy" className="h-10" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">School Motto</Label>
                <Input value={profile.motto} onChange={e => setProfile(p => ({ ...p, motto: e.target.value }))} placeholder="Excellence in Education" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block"><Mail className="w-3.5 h-3.5 inline mr-1" />Email</Label>
                <Input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} placeholder="info@school.edu.ng" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block"><Phone className="w-3.5 h-3.5 inline mr-1" />Phone</Label>
                <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+234 800 123 4567" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block"><Globe className="w-3.5 h-3.5 inline mr-1" />Website</Label>
                <Input value={profile.website} onChange={e => setProfile(p => ({ ...p, website: e.target.value }))} placeholder="www.school.edu.ng" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block"><MapPin className="w-3.5 h-3.5 inline mr-1" />City</Label>
                <Input value={profile.city} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} placeholder="Lagos" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">State</Label>
                <Input value={profile.state} onChange={e => setProfile(p => ({ ...p, state: e.target.value }))} placeholder="Lagos" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Country</Label>
                <Input value={profile.country} onChange={e => setProfile(p => ({ ...p, country: e.target.value }))} placeholder="Nigeria" className="h-10" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Address</Label>
                <Input value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} placeholder="15 Education Close, Victoria Island" className="h-10" />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#516EFA] hover:bg-[#4058e8] text-white px-6" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ── Academic Tab ────────────────────────────────────────────────── */}
        <TabsContent value="academic" className="mt-6 space-y-5">
          {/* Academic Years */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-outfit font-semibold text-slate-900">Academic Years</h3>
                <p className="text-xs text-slate-500 mt-0.5">Set the current academic year</p>
              </div>
              <AddAcademicYearButton onSuccess={loadAcademic} />
            </div>
            <div className="divide-y divide-slate-50">
              {years.length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-sm">No academic years configured</div>
              ) : years.map(y => (
                <div key={y.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#eef1ff] flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-[#516EFA]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{y.name}</p>
                      <p className="text-xs text-slate-500">{y.start_date} → {y.end_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {y.is_current ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs"><Check className="w-3 h-3 mr-1" />Current</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setCurrentYear(y.id)}>Set Current</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Terms */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-outfit font-semibold text-slate-900">Terms</h3>
                <p className="text-xs text-slate-500 mt-0.5">Set the current term</p>
              </div>
              <AddTermButton years={years} onSuccess={loadAcademic} />
            </div>
            <div className="divide-y divide-slate-50">
              {terms.length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-sm">No terms configured</div>
              ) : terms.map(t => (
                <div key={t.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.start_date} → {t.end_date}</p>
                    </div>
                  </div>
                  {t.is_current ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs"><Check className="w-3 h-3 mr-1" />Current</Badge>
                  ) : (
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setCurrentTerm(t.id)}>Set Current</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ── Grading Scale Tab ────────────────────────────────────────────── */}
        <TabsContent value="grading" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-outfit font-semibold text-slate-900">Grading Scale</h3>
                <p className="text-xs text-slate-500 mt-0.5">Nigerian WAEC standard — customise to your school</p>
              </div>
              <Button onClick={saveGrades} disabled={savingGrades} className="bg-[#516EFA] hover:bg-[#4058e8] text-white h-8 text-sm px-4" data-testid="save-grading-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingGrades ? 'Saving...' : 'Save Scale'}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Grade</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Min %</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Max %</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Remark</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {grades.map((g, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3">
                        <Input value={g.grade} onChange={e => updateGrade(i, 'grade', e.target.value)} className="h-8 w-16 text-center font-bold text-sm" />
                      </td>
                      <td className="px-5 py-3">
                        <Input type="number" value={g.min} onChange={e => updateGrade(i, 'min', e.target.value)} className="h-8 w-20" min={0} max={100} />
                      </td>
                      <td className="px-5 py-3">
                        <Input type="number" value={g.max} onChange={e => updateGrade(i, 'max', e.target.value)} className="h-8 w-20" min={0} max={100} />
                      </td>
                      <td className="px-5 py-3">
                        <Input value={g.remark} onChange={e => updateGrade(i, 'remark', e.target.value)} className="h-8 w-32" />
                      </td>
                      <td className="px-5 py-3">
                        <Badge className={`text-xs ${g.grade.startsWith('A') ? 'bg-emerald-50 text-emerald-700' : g.grade.startsWith('B') ? 'bg-blue-50 text-blue-700' : g.grade.startsWith('C') ? 'bg-amber-50 text-amber-700' : g.grade.startsWith('D') || g.grade.startsWith('E') ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}>
                          {g.grade} ({g.min}–{g.max}%)
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-slate-50 text-xs text-slate-500">
              Tip: Scores are matched from top to bottom. Make sure ranges don't overlap.
            </div>
          </div>
        </TabsContent>

        {/* ── Payments Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="payments" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="payment-settings-section">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-outfit font-semibold text-slate-900">Online Payment Gateway</h3>
                <p className="text-xs text-slate-500 mt-0.5">Enable parents to pay school fees online via Paystack or Flutterwave</p>
              </div>
              <Button onClick={savePaySettings} disabled={savingPay} className="bg-[#516EFA] hover:bg-[#4058e8] text-white h-8 text-sm px-4" data-testid="save-pay-settings-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingPay ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Gateway selector */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Preferred Gateway</Label>
                <div className="flex gap-3">
                  {[
                    { val: 'paystack', label: 'Paystack', color: 'bg-blue-600' },
                    { val: 'flutterwave', label: 'Flutterwave', color: 'bg-orange-500' },
                  ].map(g => (
                    <button key={g.val} onClick={() => setPaySettings(p => ({ ...p, gateway: g.val }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${paySettings.gateway === g.val ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid={`gateway-${g.val}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${g.color}`} />
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-px bg-slate-100" />

              {/* Paystack keys */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                  <h4 className="font-semibold text-slate-800 text-sm">Paystack Keys</h4>
                  {paySettings.has_paystack && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Configured</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Public Key (pk_...)</Label>
                    <Input value={paySettings.paystack_public_key} onChange={e => setPaySettings(p => ({ ...p, paystack_public_key: e.target.value }))}
                      placeholder="pk_live_xxxxxxxxxxxx" className="h-9 text-sm font-mono" data-testid="paystack-public-key" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                      Secret Key (sk_...) {paySettings.has_paystack && <span className="text-slate-400 normal-case font-normal">· leave blank to keep existing</span>}
                    </Label>
                    <div className="relative">
                      <Input type={showPS ? 'text' : 'password'} value={paySettings.paystack_secret_key}
                        onChange={e => setPaySettings(p => ({ ...p, paystack_secret_key: e.target.value }))}
                        placeholder={paySettings.has_paystack ? '••••••••••••••••' : 'sk_live_xxxxxxxxxxxx'}
                        className="h-9 text-sm font-mono pr-10" data-testid="paystack-secret-key" />
                      <button type="button" onClick={() => setShowPS(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPS ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-px bg-slate-100" />

              {/* Flutterwave keys */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <h4 className="font-semibold text-slate-800 text-sm">Flutterwave Keys</h4>
                  {paySettings.has_flutterwave && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Configured</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Public Key (FLWPUBK_...)</Label>
                    <Input value={paySettings.flutterwave_public_key} onChange={e => setPaySettings(p => ({ ...p, flutterwave_public_key: e.target.value }))}
                      placeholder="FLWPUBK_xxxxxxxxxxxx" className="h-9 text-sm font-mono" data-testid="flutterwave-public-key" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                      Secret Key (FLWSECK_...) {paySettings.has_flutterwave && <span className="text-slate-400 normal-case font-normal">· leave blank to keep existing</span>}
                    </Label>
                    <div className="relative">
                      <Input type={showFWS ? 'text' : 'password'} value={paySettings.flutterwave_secret_key}
                        onChange={e => setPaySettings(p => ({ ...p, flutterwave_secret_key: e.target.value }))}
                        placeholder={paySettings.has_flutterwave ? '••••••••••••••••' : 'FLWSECK_xxxxxxxxxxxx'}
                        className="h-9 text-sm font-mono pr-10" data-testid="flutterwave-secret-key" />
                      <button type="button" onClick={() => setShowFWS(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showFWS ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-xs text-amber-800">
                <strong>Security note:</strong> Secret keys are stored encrypted and never sent back to the browser. Use test keys (pk_test / sk_test) during setup. Switch to live keys when ready to accept real payments.
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Email Tab ─────────────────────────────────────────────────────── */}
        <TabsContent value="email" className="mt-6">
          {/* Push Notifications Panel */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-4">
            <div className="mb-4">
              <h3 className="font-outfit font-semibold text-slate-900">Push Notifications</h3>
              <p className="text-xs text-slate-500 mt-0.5">Receive real-time alerts on this device for admissions, payments, and attendance</p>
            </div>
            <PushNotificationsPanel />
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="email-settings-section">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-outfit font-semibold text-slate-900">Email Notifications</h3>
                <p className="text-xs text-slate-500 mt-0.5">Configure your email provider to send payment receipts and notifications to parents</p>              </div>
              <Button onClick={saveEmailSettings} disabled={savingEmail} className="bg-[#516EFA] hover:bg-[#4058e8] text-white h-8 text-sm px-4" data-testid="save-email-settings-btn">
                {savingEmail ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                {savingEmail ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Provider selector */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Email Provider</Label>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { val: 'resend', label: 'Resend', desc: 'Modern API, free tier', color: 'bg-violet-600' },
                    { val: 'smtp', label: 'SMTP', desc: 'Any email server', color: 'bg-slate-600' },
                    { val: 'none', label: 'Disabled', desc: 'No email sending', color: 'bg-slate-300' },
                  ].map(p => (
                    <button key={p.val} onClick={() => setEmailSettings(e => ({ ...e, provider: p.val }))}
                      className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${emailSettings.provider === p.val ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid={`email-provider-${p.val}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
                      <span className="font-semibold">{p.label}</span>
                      <span className="text-xs opacity-60">{p.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* From name/email — always shown when not disabled */}
              {emailSettings.provider !== 'none' && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div>
                    <Label className="text-xs font-medium text-slate-600 mb-1.5 block">From Name &amp; Email</Label>
                    <Input value={emailSettings.email_from} onChange={e => setEmailSettings(p => ({ ...p, email_from: e.target.value }))}
                      placeholder='e.g. Greenfield Academy <info@myschool.edu.ng>'
                      className="h-9 text-sm" data-testid="email-from" />
                    <p className="text-xs text-slate-400 mt-1">This is what parents see as the sender name in their inbox</p>
                  </div>
                </>
              )}

              {/* Resend fields */}
              {emailSettings.provider === 'resend' && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-violet-600" />
                      <h4 className="font-semibold text-slate-800 text-sm">Resend API Key</h4>
                      {emailSettings.has_resend && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Configured</span>}
                    </div>
                    <div className="relative">
                      <Input type={showResendKey ? 'text' : 'password'} value={emailSettings.resend_api_key}
                        onChange={e => setEmailSettings(p => ({ ...p, resend_api_key: e.target.value }))}
                        placeholder={emailSettings.has_resend ? '••••••••••••••••' : 're_xxxxxxxxxx'}
                        className="h-9 text-sm font-mono pr-10" data-testid="resend-api-key" />
                      <button type="button" onClick={() => setShowResendKey(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Get your free API key at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">resend.com</a>
                    </p>
                  </div>
                </>
              )}

              {/* SMTP fields */}
              {emailSettings.provider === 'smtp' && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                      <h4 className="font-semibold text-slate-800 text-sm">SMTP Configuration</h4>
                      {emailSettings.has_smtp && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Configured</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">SMTP Host</Label>
                        <Input value={emailSettings.smtp_host} onChange={e => setEmailSettings(p => ({ ...p, smtp_host: e.target.value }))}
                          placeholder="smtp.gmail.com" className="h-9 text-sm" data-testid="smtp-host" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Port</Label>
                        <Input type="number" value={emailSettings.smtp_port} onChange={e => setEmailSettings(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))}
                          placeholder="587" className="h-9 text-sm" data-testid="smtp-port" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Username / Email</Label>
                        <Input value={emailSettings.smtp_user} onChange={e => setEmailSettings(p => ({ ...p, smtp_user: e.target.value }))}
                          placeholder="your@email.com" className="h-9 text-sm" data-testid="smtp-user" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                          Password / App Password {emailSettings.has_smtp && <span className="text-slate-400 normal-case font-normal">· leave blank to keep existing</span>}
                        </Label>
                        <div className="relative">
                          <Input type={showSmtpPass ? 'text' : 'password'} value={emailSettings.smtp_pass}
                            onChange={e => setEmailSettings(p => ({ ...p, smtp_pass: e.target.value }))}
                            placeholder={emailSettings.has_smtp ? '••••••••••••' : 'App password'}
                            className="h-9 text-sm pr-10" data-testid="smtp-pass" />
                          <button type="button" onClick={() => setShowSmtpPass(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showSmtpPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Test email section */}
              {emailSettings.provider !== 'none' && (emailSettings.has_resend || emailSettings.has_smtp || emailSettings.resend_api_key || emailSettings.smtp_pass) && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                    <h4 className="font-semibold text-indigo-800 text-sm mb-2 flex items-center gap-2">
                      <Send className="w-4 h-4" /> Send Test Email
                    </h4>
                    <div className="flex gap-2">
                      <Input value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)}
                        placeholder="Enter email address (leave blank to send to yourself)"
                        className="h-9 text-sm flex-1" data-testid="test-email-to" />
                      <Button onClick={sendTestEmail} disabled={testingEmail}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-4 text-sm whitespace-nowrap"
                        data-testid="send-test-email-btn">
                        {testingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {testingEmail ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                    <p className="text-xs text-indigo-600 mt-1.5">Save settings first, then test to verify your configuration works</p>
                  </div>
                </>
              )}

              {emailSettings.provider === 'none' && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center text-sm text-slate-500">
                  Select Resend or SMTP above to enable email notifications for parents
                </div>
              )}

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-xs text-amber-800">
                <strong>Privacy note:</strong> Credentials are stored securely per school and never returned to the browser. For Gmail SMTP, use an App Password (not your main password).
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Modules Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="modules" className="mt-6">
          <ModulesTab />
        </TabsContent>

      </Tabs>
    </div>
  );
}

function ModulesTab() {
  const [settings, setSettings] = useState({ games_enabled: false, quiz_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/games/school/settings').then(r => {
      if (r?.success) setSettings({ games_enabled: r.data.games_enabled, quiz_enabled: r.data.quiz_enabled });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/games/school/settings', settings);
      if (res?.success) {
        const { toast: toastFn } = await import('sonner');
        toastFn.success('Module settings saved');
      }
    } catch { const { toast: t } = await import('sonner'); t.error('Failed to save'); }
    setSaving(false);
  };

  const Toggle = ({ label, desc, field }) => (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
      <div>
        <p className="font-bold text-slate-700 text-sm">{label}</p>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
      <button
        onClick={() => setSettings(s => ({ ...s, [field]: !s[field] }))}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings[field] ? 'bg-[#2563eb]' : 'bg-slate-300'}`}
        data-testid={`toggle-${field}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings[field] ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );

  if (loading) return <div className="py-8 flex justify-center"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4 shadow-sm">
        <div>
          <h3 className="font-bold text-slate-800">Module Toggles</h3>
          <p className="text-xs text-slate-400 mt-0.5">Enable or disable features for your school's students</p>
        </div>
        <Toggle label="Learning Games" desc="Allow students to play educational games" field="games_enabled" />
        <Toggle label="Quiz Zone" desc="Allow students to take quizzes and track performance" field="quiz_enabled" />
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 text-sm"
          data-testid="save-modules-btn">
          {saving ? 'Saving...' : 'Save Module Settings'}
        </button>
      </div>
    </div>
  );
}


function AddAcademicYearButton({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', is_current: false });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.start_date || !form.end_date) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      await api.post('/academics/years', form);
      toast.success('Academic year added');
      setOpen(false);
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to add'); }
    finally { setLoading(false); }
  };

  if (!open) return <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setOpen(true)}><Plus className="w-3.5 h-3.5 mr-1" />Add Year</Button>;

  return (
    <div className="flex items-center gap-2">
      <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="2025/2026" className="h-7 w-24 text-xs" />
      <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} className="h-7 w-36 text-xs" />
      <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} className="h-7 w-36 text-xs" />
      <Button size="sm" onClick={handleSave} disabled={loading} className="bg-[#516EFA] text-white h-7 text-xs px-3">{loading ? '...' : 'Save'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs">Cancel</Button>
    </div>
  );
}

function AddTermButton({ years, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', academic_year_id: '', start_date: '', end_date: '', is_current: false });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.academic_year_id) { toast.error('Name and academic year required'); return; }
    setLoading(true);
    try {
      await api.post('/academics/terms', form);
      toast.success('Term added');
      setOpen(false);
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to add'); }
    finally { setLoading(false); }
  };

  if (!open) return <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setOpen(true)}><Plus className="w-3.5 h-3.5 mr-1" />Add Term</Button>;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="First Term" className="h-7 w-28 text-xs" />
      <Select value={form.academic_year_id} onValueChange={v => setForm(p => ({ ...p, academic_year_id: v }))}>
        <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Year" /></SelectTrigger>
        <SelectContent>{years.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} className="h-7 w-36 text-xs" />
      <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} className="h-7 w-36 text-xs" />
      <Button size="sm" onClick={handleSave} disabled={loading} className="bg-[#516EFA] text-white h-7 text-xs px-3">{loading ? '...' : 'Save'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs">Cancel</Button>
    </div>
  );
}
